"use client";

import { useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { Link } from "@/i18n/navigation";

/**
 * Le code à coller pour intégrer l'outil sur un autre site.
 *
 * REPLIÉ, EN BAS, ET SANS DISCOURS. Celui qui lit la page vient calculer, pas
 * intégrer : le bloc ne se déplie que pour qui le cherche, blogueur,
 * expert-comptable ou incubateur.
 *
 * TROIS LIGNES, ET LA TROISIÈME COMPTE. L'iframe porte l'outil, le script
 * ajuste sa hauteur, et le paragraphe final est le seul lien que les moteurs
 * de recherche verront dans la page hôte : le contenu d'une iframe ne lui
 * est jamais attribué. Son ancre est la marque, pas des mots-clés : un lien
 * distribué dans un widget et bourré de mots-clés est exactement ce que
 * Google range parmi les liens artificiels.
 */

const TEXTES = {
    fr: {
        titre: "Intégrer cet outil sur votre site",
        aide: "Gratuit, sans compte. Collez ce code dans votre page : l'outil s'affiche en entier et s'ajuste à sa hauteur. Les chiffres sont mis à jour chez nous, sans rien à faire de votre côté.",
        copier: "Copier le code",
        copie: "Code copié",
        un: "un outil",
        conditions: "En intégrant cet outil, vous acceptez nos ",
        conditionsLien: "conditions d'utilisation des outils",
    },
    en: {
        titre: "Embed this tool on your site",
        aide: "Free, no account needed. Paste this code into your page: the tool shows in full and adjusts to its height. The figures are kept up to date on our side, with nothing to do on yours.",
        copier: "Copy the code",
        copie: "Code copied",
        un: "a tool by",
        conditions: "By embedding this tool, you accept our ",
        conditionsLien: "terms of use for the tools",
    },
};

export default function ToolsEmbedCode({ origine, locale, slug, nom, pageComplete }) {
    const T = TEXTES[locale] || TEXTES.fr;
    const [copie, setCopie] = useState(false);

    const echapper = (v) => v.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
    const code = [
        `<iframe src="${origine}/embed/${locale}/${slug}" title="${echapper(nom)}" width="100%" height="720" style="border:0;max-width:760px" loading="lazy"></iframe>`,
        `<script async src="${origine}/static/embed-v1.js"></script>`,
        `<p>${echapper(nom)}, ${T.un} <a href="${origine}${pageComplete}">Believemy</a></p>`,
    ].join("\n");

    const copier = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopie(true);
            setTimeout(() => setCopie(false), 2000);
        } catch {
            setCopie(false);
        }
    };

    return (
        <details className="group mt-14 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] px-5">
            <summary className="flex items-center justify-between gap-4 py-4 cursor-pointer list-none font-semibold dark:text-white">
                <span className="flex items-center gap-2.5">
                    <SolarIcon name="code-square-linear" className="w-5 h-5 text-gray-400 dark:text-lightBlue/60 shrink-0" />
                    {T.titre}
                </span>
                <SolarIcon name="alt-arrow-down-linear" className="h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 group-open:rotate-180 group-open:text-primary" />
            </summary>
            <div className="pb-5">
                <p className="text-sm text-gray-600 dark:text-lightBlue mb-3">{T.aide}</p>
                <pre className="text-xs leading-5 whitespace-pre-wrap break-all rounded-lg bg-gray-50 dark:bg-white/[0.04] border border-gray-200/70 dark:border-white/10 p-3 text-gray-700 dark:text-lightBlue">
                    {code}
                </pre>
                <button
                    type="button"
                    onClick={copier}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-white/15 px-3.5 py-2 text-sm font-semibold dark:text-white hover:border-primary/50 transition-colors"
                >
                    <SolarIcon name={copie ? "check-circle-linear" : "copy-linear"} className="w-4 h-4" />
                    {copie ? T.copie : T.copier}
                </button>
                <span role="status" className="sr-only">
                    {copie ? T.copie : ""}
                </span>
                <p className="mt-3 mb-0 text-xs leading-5 text-gray-500 dark:text-lightBlue/70">
                    {T.conditions}
                    <Link href="/tools-terms" className="text-[#B83C12] dark:text-[#FF8A5B] underline underline-offset-2 hover:text-[#B83C12] dark:hover:text-[#FF8A5B]">
                        {T.conditionsLien}
                    </Link>
                    .
                </p>
            </div>
        </details>
    );
}
